import { Injectable } from '@jack-henry/frontend-utils/di';
import { NavigationsService } from '@treasury/domain/navigation';
import {
    FeatureVisibilityService,
    PwaVisibilityFeature,
} from '@treasury/domain/services/feature-visibility';

@Injectable()
export class FeatureSearchService {
    constructor(
        private navService: NavigationsService,
        private featureVisibilityService: FeatureVisibilityService
    ) {
        this.navigationItemsPromise = this.navService.getFlattenedNavigationOptions();
        this.getNavigationItems();
    }

    public navigationItems: string[] = [];

    public filteredNavigationItems: {
        label: string;
        value: { route: string; navigationItem: string; feature: PwaVisibilityFeature | undefined };
    }[] = [];

    public allAvailableNavigationItems: {
        label: string;
        value: { route: string; navigationItem: string; feature: PwaVisibilityFeature | undefined };
    }[] = [];

    private navigationItemsPromise: Promise<string[]>;

    public async getNavigationItems() {
        const items = await this.navigationItemsPromise;
        this.navigationItems = items;
        this.mapAllPossibleNavigationItemsToLabelText();
    }

    private isFeatureVisible(feature: PwaVisibilityFeature): boolean {
        return this.featureVisibilityService
            ? this.featureVisibilityService.isFeatureVisible(feature)
            : false;
    }

    private filterAllPossibleNavigationItems() {
        this.filteredNavigationItems = this.allAvailableNavigationItems.filter(item => {
            if (!item.value.feature) {
                return true;
            }
            return this.isFeatureVisible(item.value.feature);
        });
    }

    private mapAllPossibleNavigationItemsToLabelText() {
        // Note: keeping commented out features that still need to be added for functional parity
        this.allAvailableNavigationItems = [
            { item: 'dashboard', text: 'View dashboard', route: '/dashboard' },
            {
                item: 'accounts',
                text: 'See all available accounts',
                route: '/accounts',
                feature: PwaVisibilityFeature.Accounts,
            },
            {
                item: 'pendingApprovals',
                text: 'View ACH payments pending approval',
                route: '/pending-approvals/ach',
                feature: PwaVisibilityFeature.PendingApprovalsAch,
            },
            {
                item: 'pendingApprovals',
                text: 'View wire payments pending approval',
                route: '/pending-approvals/wire',
                feature: PwaVisibilityFeature.PendingApprovalsWires,
            },
            {
                item: 'pendingApprovals',
                text: 'View transfer payments pending approval',
                route: '/pending-approvals/transfer',
                feature: PwaVisibilityFeature.PendingApprovalsTransfers,
            },
            {
                item: 'pendingApprovals',
                text: 'View loan payments pending approval',
                route: '/pending-approvals/loanpayment',
                feature: PwaVisibilityFeature.PendingApprovalsTransfers,
            },
            {
                item: 'pendingApprovals',
                text: 'View stop payments pending approval',
                route: '/pending-approvals/stoppayment',
                feature: PwaVisibilityFeature.StopPayments,
            },
            {
                item: 'pendingApprovals',
                text: 'View users pending approval',
                route: '/pending-approvals/user',
                feature: PwaVisibilityFeature.PendingApprovalsUsers,
            },
            {
                item: 'researchTransactions',
                text: 'Research all transactions',
                route: '/accounts/research-transactions',
                feature: PwaVisibilityFeature.Accounts,
            },
            // {
            //     item: 'agileticsWebEscrow',
            //     text: 'Agiletics Web Escrow',
            //     route: '/agileticsWebEscrow',
            // },
            // { item: 'derivativePath', text: 'Derivative Path', route: '/derivativePath' },
            {
                item: 'billPay',
                text: 'See bill pay payments',
                route: '/business-bill-pay',
                feature: PwaVisibilityFeature.BillPay,
            },
            {
                item: 'billPay',
                text: 'View bill pay payees',
                route: '/business-bill-pay/payees',
                feature: PwaVisibilityFeature.BillPay,
            },
            {
                item: 'createTransfer',
                text: 'Create a transfer',
                route: '/transfers/payment/create/new',
                feature: PwaVisibilityFeature.Transfers,
            },
            {
                item: 'createTransferTemplates',
                text: 'Create a transfer template',
                route: '/transfers/template/create/new/template',
                feature: PwaVisibilityFeature.Transfers,
            },
            {
                item: 'transferList',
                text: 'View transfers',
                route: '/transfers',
                feature: PwaVisibilityFeature.Transfers,
            },
            {
                item: 'transferTemplateList',
                text: 'View transfer templates',
                route: '/transfers/templates',
                feature: PwaVisibilityFeature.Transfers,
            },
            {
                item: 'createLoanPayment',
                text: 'Create a loan payment',
                route: '/loans/payment/create/new',
                feature: PwaVisibilityFeature.Loans,
            },
            {
                item: 'loanPaymentList',
                text: 'View loan payments',
                route: '/loans',
                feature: PwaVisibilityFeature.Loans,
            },
            {
                item: 'createWire',
                text: 'Create a wire payment',
                route: '/wires/domestic/select/new/usd',
                feature: PwaVisibilityFeature.Wires,
            },
            {
                item: 'createWireFromTemplate',
                text: 'Create a wire payment from template',
                route: '/wires/templates/initiate',
                feature: PwaVisibilityFeature.Wires,
            },
            // { item: 'uploadWires', text: 'Upload Wires', route: '/uploadWires' },
            // {
            //     item: 'createCorpayWire',
            //     text: 'Create Corpay Wire',
            //     route: '/createCorpayWire',
            // },
            {
                item: 'wireList',
                text: 'View wire payments',
                route: '/wires',
                feature: PwaVisibilityFeature.Wires,
            },
            // {
            //     item: 'wireFileActivity',
            //     text: 'Wire File Activity',
            //     route: '/wireFileActivity',
            // },
            // {
            //     item: 'recurringWireList',
            //     text: 'Recurring Wire List',
            //     route: '/recurringWireList',
            // },
            // {
            //     item: 'wireTemplateList',
            //     text: 'Wire Template List',
            //     route: '/wireTemplateList',
            // },
            {
                item: 'creditorList',
                text: 'View wire creditors',
                route: '/wires/creditors',
                feature: PwaVisibilityFeature.Wires,
            },
            {
                item: 'creditorCreate',
                text: 'Create a wire creditor',
                route: '/wires/creditors/new',
                feature: PwaVisibilityFeature.Wires,
            },
            // {
            //     item: 'wireUploadFormats',
            //     text: 'Wire Upload Formats',
            //     route: '/wireUploadFormats',
            // },
            // {
            //     item: 'achCreatePayment',
            //     text: 'ACH Create Payment',
            //     route: '/achCreatePayment',
            // },
            // {
            //     item: 'achCreateTaxPayment',
            //     text: 'ACH Create Tax Payment',
            //     route: '/achCreateTaxPayment',
            // },
            // {
            //     item: 'createChildSupportPayment',
            //     text: 'Create Child Support Payment',
            //     route: '/createChildSupportPayment',
            // },
            // {
            //     item: 'createInternationalAchPayment',
            //     text: 'Create International ACH Payment',
            //     route: '/createInternationalAchPayment',
            // },
            // {
            //     item: 'fileActivityList',
            //     text: 'File Activity List',
            //     route: '/fileActivityList',
            // },
            {
                item: 'paymentList',
                text: 'View ACH payments activity',
                route: '/ach',
                feature: PwaVisibilityFeature.ACH,
            },
            // {
            //     item: 'recurringAchPaymentList',
            //     text: 'Recurring ACH Payment List',
            //     route: '/recurringAchPaymentList',
            // },
            // { item: 'batchList', text: 'Batch List', route: '/batchList' },
            // {
            //     item: 'achTaxTemplateList',
            //     text: 'ACH Tax Template List',
            //     route: '/achTaxTemplateList',
            // },
            // {
            //     item: 'childSupportTemplateList',
            //     text: 'Child Support Template List',
            //     route: '/childSupportTemplateList',
            // },
            // {
            //     item: 'internationalTemplateList',
            //     text: 'International Template List',
            //     route: '/internationalTemplateList',
            // },
            // { item: 'recipientsList', text: 'Recipients List', route: '/recipientsList' },
            // {
            //     item: 'recipientActivity',
            //     text: 'Recipient Activity',
            //     route: '/recipientActivity',
            // },
            // { item: 'importLayout', text: 'Import Layout', route: '/importLayout' },
            // {
            //     item: 'achReturnReview',
            //     text: 'ACH Return Review',
            //     route: '/achReturnReview',
            // },
            // {
            //     item: 'achNotificationOfChangeNacha',
            //     text: 'ACH Notification Of Change Nacha',
            //     route: '/achNotificationOfChangeNacha',
            // },
            // { item: 'achReturnNacha', text: 'ACH Return Nacha', route: '/achReturnNacha' },
            {
                item: 'arpExceptionsList',
                text: 'View check exceptions',
                route: '/positive-pay/check-exceptions',
                feature: PwaVisibilityFeature.PosPayCheckExceptions,
            },
            // {
            //     item: 'checkExceptionsDecisionActivityList',
            //     text: 'Check Exceptions Decision Activity List',
            //     route: '/checkExceptionsDecisionActivityList',
            // },
            {
                item: 'achExceptionsList',
                text: 'View ACH exceptions',
                route: '/positive-pay/ach-exceptions',
                feature: PwaVisibilityFeature.PosPayAchExceptions,
            },
            // {
            //     item: 'achExceptionsDecisionActivityList',
            //     text: 'ACH Exceptions Decision Activity List',
            //     route: '/achExceptionsDecisionActivityList',
            // },
            // { item: 'arpCreate', text: 'ARP Create', route: '/arpCreate' },
            // { item: 'arpActivity', text: 'ARP Activity', route: '/arpActivity' },
            // {
            //     item: 'issuedItemsFileActivity',
            //     text: 'Issued Items File Activity',
            //     route: '/issuedItemsFileActivity',
            // },
            // {
            //     item: 'arpUploadFormatList',
            //     text: 'ARP Upload Format List',
            //     route: '/arpUploadFormatList',
            // },
            {
                item: 'stpCreate',
                text: 'Create a stop payment',
                route: '/stop-payments/payment/create',
                feature: PwaVisibilityFeature.StopPayments,
            },
            {
                item: 'stpList',
                text: 'View stop payments',
                route: '/stop-payments',
                feature: PwaVisibilityFeature.StopPayments,
            },
            {
                item: 'businessBillPay_frame',
                text: 'View bill pay payments',
                route: '/business-bill-pay',
                feature: PwaVisibilityFeature.BillPay,
            },
            {
                item: 'businessBillPay',
                text: 'Create a bill pay payment',
                route: '/business-bill-pay/payments/create/new',
                feature: PwaVisibilityFeature.BillPay,
            },
            {
                item: 'businessBillPay',
                text: 'View bill pay payees',
                route: 'business-bill-pay/payees',
                feature: PwaVisibilityFeature.BillPay,
            },
            // { item: 'receivables', text: 'Receivables', route: '/receivables' },
            {
                item: 'rdc',
                text: 'View remote deposits',
                route: '/deposit',
                feature: PwaVisibilityFeature.RDC,
            },
            {
                item: 'irAll',
                text: 'View reports',
                route: '/reports',
                feature: PwaVisibilityFeature.Reports,
            },
            // { item: 'irFavorite', text: 'IR Favorite', route: '/irFavorite' },
            // { item: 'irCustom', text: 'IR Custom', route: '/irCustom' },
            // { item: 'irStandard', text: 'IR Standard', route: '/irStandard' },
            // {
            //     item: 'electronicDocuments_frame',
            //     text: 'Electronic Documents Frame',
            //     route: '/electronicDocuments_frame',
            // },
            // {
            //     item: 'wausauStatements',
            //     text: 'Wausau Statements',
            //     route: '/wausauStatements',
            // },
            // {
            //     item: 'accountReconciliation',
            //     text: 'Account Reconciliation',
            //     route: '/accountReconciliation',
            // },
            // { item: 'userList', text: 'User List', route: '/userList' },
            // { item: 'createUser', text: 'Create User', route: '/createUser' },
            // {
            //     item: 'accountNicknames',
            //     text: 'Account Nicknames',
            //     route: '/accountNicknames',
            // },
        ].map(item => {
            return {
                label: item.text,
                value: { route: item.route, navigationItem: item.item, feature: item.feature },
            };
        });

        this.filterAllPossibleNavigationItems();
    }
}
